import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { IdentityService } from '@medprompt/shared';
import '@medprompt/shared/src/common/modals.css';
import './tabs/MessagingModals.css';

const TITLE_MAX_LENGTH = 50;
const CONTENT_MAX_LENGTH = 1000;

const TemplateEditorModal = ({ template, onSave, onClose }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [clinicName, setClinicName] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  const identityService = useMemo(() => new IdentityService(), []);
  const isEditMode = template !== null;
  const storageKey = 'medprompt_message_template';
  
  // Example greeting and signature for preview
  const greeting = 'Dear Mr/Mrs [Surname],\n\n';
  const signature = `\n\nKind regards,\n${clinicName || 'Your Practice'}`;

  // Save draft to session storage when creating new template (not editing)
  // Only save after initial load is complete to avoid overwriting with empty state
  useEffect(() => {
    if (!isEditMode && isInitialized) {
      try {
        const draft = {
          title,
          content
        };
        sessionStorage.setItem(storageKey, JSON.stringify(draft));
      } catch (error) {
        console.error('Failed to save template draft to session storage:', error);
      }
    }
  }, [title, content, isEditMode, isInitialized]);

  // Fetch clinic name on mount
  useEffect(() => {
    const fetchClinicName = async () => {
      try {
        const clinics = await identityService.fetchClinics();
        const selectedId = sessionStorage.getItem('selectedClinicId');
        const clinic = clinics?.find(c => c.clinicId === selectedId) || clinics?.[0];
        setClinicName(clinic?.clinicName || '');
      } catch (error) {
        console.error('Failed to fetch clinic:', error);
      }
    };
    fetchClinicName();
  }, []);

  // Initialize form with template data if editing, or load draft if creating
  useEffect(() => {
    if (template) {
      // Edit mode: load existing template data
      setTitle(template.title || '');
      setContent(template.content || '');
    } else {
      // Create mode: load draft from session storage
      try {
        const saved = sessionStorage.getItem(storageKey);
        if (saved) {
          const draft = JSON.parse(saved);
          if (draft.title) {
            setTitle(draft.title);
          }
          if (draft.content) {
            setContent(draft.content);
          }
        }
      } catch (error) {
        console.error('Failed to load template draft from session storage:', error);
      }
    }
    // Mark as initialized after loading (or attempting to load)
    setIsInitialized(true);
  }, [template]);

  const validateForm = () => {
    const newErrors = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    } else if (title.length > TITLE_MAX_LENGTH) {
      newErrors.title = `Title must be ${TITLE_MAX_LENGTH} characters or less`;
    }

    if (!content.trim()) {
      newErrors.content = 'Content is required';
    } else if (content.length > CONTENT_MAX_LENGTH) {
      newErrors.content = `Content must be ${CONTENT_MAX_LENGTH} characters or less`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    
    // Create template object
    const templateData = {
      title: title.trim(),
      content: content.trim()
    };

    // Call the onSave callback
    onSave(templateData);
    
    // Clear draft from session storage after creating new template (not editing)
    if (!isEditMode) {
      try {
        sessionStorage.removeItem(storageKey);
      } catch (error) {
        console.error('Failed to clear template draft from session storage:', error);
      }
    }
    
    setIsSaving(false);
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    if (newTitle.length <= TITLE_MAX_LENGTH) {
      setTitle(newTitle);
      if (errors.title) {
        setErrors({ ...errors, title: null });
      }
    }
  };

  const handleContentChange = (e) => {
    const newContent = e.target.value;
    if (newContent.length <= CONTENT_MAX_LENGTH) {
      setContent(newContent);
      if (errors.content) {
        setErrors({ ...errors, content: null });
      }
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container create-message-modal modal-animated" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Template' : 'Create New Template'}</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="modal-content create-message-content">
          {/* Title Input */}
          <div className="message-content-section">
            <label className="section-label">Template Title</label>
            <input
              type="text"
              className="template-title-input"
              value={title}
              onChange={handleTitleChange}
              placeholder="Enter template title..."
              disabled={isSaving}
              maxLength={TITLE_MAX_LENGTH}
            />
            <div className="character-count" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: errors.title ? '#dc3545' : '#999', fontSize: '12px' }}>
                {errors.title || ''}
              </span>
              <span>{title.length} / {TITLE_MAX_LENGTH} characters</span>
            </div>
          </div>

          {/* Content Textarea with Preview Frame */}
          <div className="message-content-section">
            <label className="section-label">Template Content</label>
            <div className="template-preview-note">
              Preview shows how the message will appear with greeting and signature. Only edit the middle section.
            </div>
            <div className="message-compose-container">
              <div className="message-frozen-section message-greeting">
                {greeting}
              </div>
              <textarea
                className="message-textarea-editable"
                value={content}
                onChange={handleContentChange}
                placeholder="Enter template content..."
                disabled={isSaving}
                rows={8}
              />
              <div className="message-frozen-section message-signature">
                {signature}
              </div>
            </div>
            <div className="character-count" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: errors.content ? '#dc3545' : '#999', fontSize: '12px' }}>
                {errors.content || ''}
              </span>
              <span>{content.length} / {CONTENT_MAX_LENGTH} characters</span>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button className="cancel-button" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          
          <button 
            className="submit-button" 
            onClick={handleSave}
            disabled={isSaving || !title.trim() || !content.trim()}
          >
            {isSaving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <span>{isEditMode ? 'Update' : 'Create'} Template</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(TemplateEditorModal);
